import { questionQuiz } from "../actions/questionQuiz";
import { scrollToChatBottom } from "../actions/scrollToChatBottom";
import { sampleActions } from "../actions/sampleActions";
import { audiocall } from "../actions/audiocall";
import { videocall } from "../../services/sts/videocall";
import { playMessage } from "../actions/playMessage";
import { pinMessage } from "../actions/pinMessage";
import { evaluateMessage } from "../actions/evaluateMessage";
import { deleteMessage } from "../actions/deleteMessage";
import { submitMessage } from "../actions/submitMessage";
import { recordAudio } from "../actions/recordAudio";
import { renderAudio } from "../actions/renderAudio";
import { shareImage } from "../actions/shareImage";
import { chatSettingsBtn } from "../actions/chatSettingsBtn";
import { chatMoreOptions } from "../actions/chatMoreOptions";
import { initFancybox } from "../../../../../../themes/growtype/resources/scripts/plugins/fancybox/initFancybox";
import { initGrowtypeSidebarEvent } from "../events/initGrowtypeSidebarEvent";
import { emojiPicker } from "./emojiPicker";
import { customUrlQueryMessage } from "./customUrlQueryMessage";
import { updateCredits } from "../actions/updateCredits";
import { contentRenderedEvent } from "../events/contentRenderedEvent";
import { chatSettings } from "./chatSettings";
import { convertMessageToImage } from "../actions/convertMessageToImage";
import { loadMessagesOneByOne } from "../actions/loadMessagesOneByOne";
import { giftPicker } from "./giftPicker";
import { evaluateChatHeight } from "../actions/evaluateChatHeight";
import { GenerateContent } from "../actions/generateContent";
import { messageActions } from "./messageActions";
import { shareMessage } from "../actions/shareMessage";
import { infoModal } from "./infoModal";
import { renderMessage } from "../actions/renderMessage";
import { appendUserMessage } from "../actions/appendUserMessage";
import { messageQueue } from "../actions/messageQueue";
import { mediaReady } from "../actions/mediaReady";
import { initVideoLoaders } from "../actions/initVideoLoaders";
import { clearSession } from "../actions/clearSession";
import { duplicateSession } from "../actions/duplicateSession";
import { chatMysteryBox } from "./chatMysteryBox";
import { regenerateWithFeedbackMessage } from "../actions/regenerateWithFeedbackMessage";
import {
  maybeImportGuestLocalHistory,
  restoreGuestLocalHistory,
  shouldUseGuestLocalHistoryMode,
} from "../helpers/guestLocalHistory";
import { chatDebugLog } from "../helpers/chatDebug";
import { clearPendingChatInput } from "../helpers/clearPendingChatInput";

function resolveDisableOneByOneLoadingAnimation(parsedArgs = {}) {
  const globalValue =
    window.growtype_chat_disable_one_by_one_loading_animation ??
    window.growtypeChatDisableOneByOneLoadingAnimation ??
    window.growtypeChatSettings?.disableOneByOneLoadingAnimation;

  if (typeof globalValue !== "undefined") {
    return Boolean(globalValue);
  }

  return Boolean(parsedArgs.disable_one_by_one_loading_animation);
}

function buildRememberedRequestContextKey(parsedArgs = {}) {
  const canonicalKey =
    typeof parsedArgs.request_context_key === "string"
      ? parsedArgs.request_context_key.trim()
      : "";

  if (canonicalKey) {
    return canonicalKey;
  }

  const context = {};
  const introQuiz =
    parsedArgs.intro_quiz || parsedArgs.roleplay_quiz_slug || "";
  const gcwm = parsedArgs.gcwm || "";

  if (introQuiz) {
    context.intro_quiz = String(introQuiz);
  }

  if (gcwm) {
    context.gcwm = String(gcwm);
  }

  return Object.keys(context).length > 0 ? JSON.stringify(context) : "";
}

function buildChatContentRequestArgs(parsedArgs = {}) {
  const requestArgs = { ...parsedArgs };

  delete requestArgs.available_backgrounds;
  delete requestArgs.bot_profile;
  requestArgs.omit_client_state_payload = true;

  return requestArgs;
}

function buildChatContentRequestSignature(parsedArgs = {}) {
  const requestArgs = buildChatContentRequestArgs(parsedArgs);

  return JSON.stringify({
    session_token: String(requestArgs.session_token || ""),
    roleplay_scenario: String(requestArgs.roleplay_scenario || ""),
    request_context_key: buildRememberedRequestContextKey(parsedArgs),
  });
}

function getChatContentRequestGeneration(contentWrapper) {
  const generation = parseInt(
    contentWrapper.attr("data-chat-content-request-generation") || "0",
    10
  );

  return Number.isFinite(generation) ? generation : 0;
}

function rememberPendingChatContentRequest(contentWrapper, xhr) {
  const pendingRequests = contentWrapper.data("chatContentPendingRequests") || [];
  pendingRequests.push(xhr);
  contentWrapper.data("chatContentPendingRequests", pendingRequests);
}

function forgetPendingChatContentRequest(contentWrapper, xhr) {
  const pendingRequests = (contentWrapper.data("chatContentPendingRequests") || []).filter(
    (candidate) => candidate !== xhr
  );
  contentWrapper.data("chatContentPendingRequests", pendingRequests);
}

function isChatContentRequestStale(
  contentWrapper,
  requestGeneration,
  requestSignature
) {
  return (
    getChatContentRequestGeneration(contentWrapper) !== requestGeneration ||
    (contentWrapper.attr("data-chat-content-request-signature") || "") !==
      requestSignature
  );
}

export function syncChatContentRequestContext(contentWrapper, parsedArgs = {}) {
  if (!contentWrapper?.length) {
    return "";
  }

  const requestSignature = buildChatContentRequestSignature(parsedArgs);
  contentWrapper.attr("data-chat-content-request-signature", requestSignature);

  if (!contentWrapper.attr("data-chat-content-request-generation")) {
    contentWrapper.attr("data-chat-content-request-generation", "0");
  }

  return requestSignature;
}

export function invalidateChatContentRequests(contentWrapper, options = {}) {
  if (!contentWrapper?.length) {
    return 0;
  }

  const shouldAbort = options.abort !== false;
  const nextGeneration = getChatContentRequestGeneration(contentWrapper) + 1;
  const pendingRequests = contentWrapper.data("chatContentPendingRequests") || [];

  contentWrapper.attr(
    "data-chat-content-request-generation",
    String(nextGeneration)
  );

  if (shouldAbort) {
    pendingRequests.forEach((xhr) => {
      if (xhr && typeof xhr.abort === "function" && xhr.readyState !== 4) {
        xhr.abort("stale_context");
      }
    });
  }

  contentWrapper.data("chatContentPendingRequests", []);

  chatDebugLog("chatContent", "[chat-debug] chatContent:invalidate-requests", {
    generation: nextGeneration,
    pendingCount: pendingRequests.length,
    aborted: shouldAbort,
    reason: options.reason || "",
  });

  return nextGeneration;
}

export function chatContent() {
  // If the LiteSpeed shell fast-path is active, wait for the shell hydration
  // AJAX (growtype_chat_shell_init) to finish patching data-args before we
  // read them.  On normal non-shell pages _growtypeChatShellPending is never
  // set so we fall through immediately.
  if (window._growtypeChatShellPending) {
    document.addEventListener(
      "growtype_chat_shell_ready",
      function onShellReady() {
        document.removeEventListener("growtype_chat_shell_ready", onShellReady);
        _chatContentInit();
      }
    );
    return;
  }
  _chatContentInit();
}

function _chatContentInit() {
  chatDebugLog('guestHistory', "[guest-history] chatContent:init", {
    shellPending: !!window._growtypeChatShellPending,
    guestLocalHistoryEnabled:
      !!window.growtype_chat_ajax?.guest_local_history_enabled,
    isLoggedIn: !!window.growtype_chat_ajax?.is_logged_in,
  });

  /**
   * Open window remove
   */
  $(".g-chatform").click(function () {
    $(".g-chatform .sample-message").removeClass("is-active");

    $(".g-chatform .g-chatform-actions-groups")
      .find(".sample-message-content.is-cloned")
      .fadeOut(300)
      .promise()
      .done(function () {
        $(this).remove();
      });
  });

  initMoreOptions();

  /**
   * Chat content render
   */
  let chatContentRender = $(".g-chatform-content-render");
  if (chatContentRender.length > 0) {
    let args = chatContentRender.attr("data-args");
    let contentWrapper = chatContentRender.closest(".g-chatform");

    // Parse args to read config flags
    let parsedArgs = {};
    try {
      parsedArgs = args ? JSON.parse(args) : {};
    } catch (e) {}

    const requestArgs = buildChatContentRequestArgs(parsedArgs);
    const serializedRequestArgs = JSON.stringify(requestArgs);
    const requestSignature = syncChatContentRequestContext(
      contentWrapper,
      parsedArgs
    );

    const disableOneByOne =
      resolveDisableOneByOneLoadingAnimation(parsedArgs);
    const rememberedRequestContextKey =
      buildRememberedRequestContextKey(parsedArgs);

    if (rememberedRequestContextKey) {
      contentWrapper.attr(
        "data-request-context-key",
        rememberedRequestContextKey
      );
    } else {
      contentWrapper.removeAttr("data-request-context-key");
    }

    chatDebugLog('chatContent', "[chat-debug] chatContent:config", {
      rawArgs: args,
      parsedDisableOneByOne:
        parsedArgs.disable_one_by_one_loading_animation,
      globalDisableOneByOne:
        window.growtype_chat_disable_one_by_one_loading_animation ??
        window.growtypeChatDisableOneByOneLoadingAnimation ??
        window.growtypeChatSettings?.disableOneByOneLoadingAnimation,
      resolvedDisableOneByOne: disableOneByOne,
      rememberedRequestContextKey,
    });

    let limit = 20;
    let offset = 0;
    let isLoadingMore = false;
    let allMessagesLoaded = false;

    function loadMessages(isLoadMore = false) {
      const requestGeneration = getChatContentRequestGeneration(contentWrapper);

      chatDebugLog('guestHistory', "[guest-history] chatContent:loadMessages:start", {
        isLoadMore,
        isLoadingMore,
        allMessagesLoaded,
        limit,
        offset,
        requestGeneration,
        requestSignature,
      });

      if (isLoadingMore || (isLoadMore && allMessagesLoaded)) return;

      isLoadingMore = true;
      if (isLoadMore) {
        contentWrapper
          .find(".g-chatform-content")
          .prepend(
            '<div class="g-chat-loader-more"><span class="spinner-border"></span></div>'
          );
      }

      let xhr = null;
      const requestOptions = {
        url: growtype_chat_ajax.url,
        type: "post",
        data: {
          action: "chat_content_render",
          args: serializedRequestArgs,
          nonce: growtype_chat_ajax.nonce,
          limit: limit,
          offset: offset,
        },
        success: function (response) {
          if (
            isChatContentRequestStale(
              contentWrapper,
              requestGeneration,
              requestSignature
            )
          ) {
            chatDebugLog("chatContent", "[chat-debug] chatContent:stale-success-ignored", {
              isLoadMore,
              limit,
              offset,
              requestGeneration,
              requestSignature,
            });
            return;
          }

          chatDebugLog('guestHistory', "[guest-history] chatContent:loadMessages:success", {
            isLoadMore,
            hasRender: !!response?.data?.render,
            newSessionCreated: !!response?.data?.new_session_created,
            existingCredits: response?.data?.existing_credits,
          });

          let chatContentHtml = response.data.render;
          let newSessionCreated = response.data.new_session_created;

          let tempDiv = $("<div>").html(chatContentHtml);
          let messageCount = tempDiv.find(".g-chatform-message").length;
          const hasBackendPagination =
            typeof response.data.has_more_messages === "boolean" &&
            Number.isFinite(Number(response.data.next_offset));

          chatDebugLog('guestHistory', "[guest-history] chatContent:loadMessages:messageCount", {
            isLoadMore,
            messageCount,
            limit,
            hasMoreMessages: response.data.has_more_messages,
            nextOffset: response.data.next_offset,
          });

          allMessagesLoaded = hasBackendPagination
            ? !response.data.has_more_messages
            : messageCount < limit;

          /**
           * Update credits
           */
          updateCredits(response.data.existing_credits);

          if (isLoadMore) {
            // Remove loader
            contentWrapper.find(".g-chat-loader-more").remove();

            // Get current scroll height and top to maintain position
            let chatContainer = contentWrapper.find(".g-chatform-content");
            let oldScrollHeight = chatContainer[0].scrollHeight;
            let oldScrollTop = chatContainer.scrollTop();

            // Extract messages from response and prepend
            // The response contains the full chat wrapper, we need to extract messages
            // But wait, the backend returns the FULL render including header/footer?
            // Yes, chat_content_render_callback returns the whole shortcode output.
            // This is inefficient for "load more".
            // We should ideally have a separate endpoint for just messages.
            // But to stick to the plan without breaking backend structure:
            // We extract .g-chatform-message elements from the response.

            let newMessages = tempDiv.find(
              ".g-chatform-content .g-chatform-message"
            );

            if (newMessages.length > 0) {
              chatContainer.prepend(newMessages);

              // Restore scroll position
              let newScrollHeight = chatContainer[0].scrollHeight;
              chatContainer.scrollTop(
                newScrollHeight - oldScrollHeight + oldScrollTop
              );

              // Re-init actions for new messages
              initMessageActions(contentWrapper, newMessages);
            }

          } else {
            /**
             * Initial Render
             */
            const previousChatId = contentWrapper.attr("id");
            const previousChatConfig =
              previousChatId && window.growtype_chat
                ? window.growtype_chat[previousChatId] || null
                : null;
            // The initial page render already contains the encrypted character
            // profile. It is intentionally omitted from chat_content_render to
            // keep the AJAX request small, so retain it before the returned
            // inline config replaces window.growtype_chat[chatId].
            const preservedBotProfile =
              parsedArgs.bot_profile ||
              (previousChatConfig
                ? previousChatConfig["bot_profile"] || ""
                : "");

            chatContentRender.replaceWith(chatContentHtml);

            const refreshedChatId = contentWrapper.attr("id");
            if (refreshedChatId) {
              const returnedChatConfig =
                response.data.chat_config &&
                typeof response.data.chat_config === "object"
                  ? response.data.chat_config
                  : {};
              window.growtype_chat = window.growtype_chat || {};
              window.growtype_chat[refreshedChatId] = Object.assign(
                {},
                returnedChatConfig,
                window.growtype_chat[refreshedChatId] || {}
              );

              if (preservedBotProfile) {
                window.growtype_chat[refreshedChatId]["bot_profile"] =
                  preservedBotProfile;
              }
            }

            // Immediately scroll to bottom BEFORE content becomes visible
            // This prevents the visual jump from top to bottom
            const chatContainer = contentWrapper.find(".g-chatform-content");
            if (chatContainer.length > 0) {
              chatContainer.scrollTop(chatContainer[0].scrollHeight);
            }

            // Re-select contentWrapper as it might have been replaced/updated
            // Actually chatContentRender was replaced, contentWrapper (parent) should be fine?
            // Wait, chatContentRender is INSIDE contentWrapper?
            // HTML: <div class='g-chatform-content-render'>...</div>
            // Replaced with: $render (which is the whole chat HTML?)

            // Let's check the backend.
            // Backend: ob_start(); ... echo growtype_chat_include_view('modal.chat-settings' ... echo <div class="g-chatform-header"> ... <div class="g-chatform-content"> ...
            // It renders the INNER content of the shortcode div?
            // No, shortcode outputs: <div id=".." class="g-chatform ..."> <div class="g-chatform-content-render">...</div> </div>
            // The AJAX replaces .g-chatform-content-render with...
            // The backend renders: modals, header, content, footer.
            // So it replaces the placeholder with the actual chat UI.

            // So contentWrapper (the .g-chatform container) now contains the rendered HTML.
            // We need to re-find elements.

            const messages = $(contentWrapper).find(
              ".g-chatform-message:visible"
            );

            chatDebugLog('guestHistory', "[guest-history] chatContent:initial-render-complete", {
              visibleMessageCount: messages.length,
              shouldUseGuestLocalHistoryMode: shouldUseGuestLocalHistoryMode(),
            });

            if (!shouldUseGuestLocalHistoryMode()) {
              maybeImportGuestLocalHistory(contentWrapper);
            }

          const restoredGuestHistory = restoreGuestLocalHistory(contentWrapper);

          chatDebugLog('guestHistory', "[guest-history] chatContent:restore-result", {
            restoredGuestHistory,
            domMessageCount: $(contentWrapper).find(".g-chatform-message").length,
          });

          chatDebugLog('chatContent', "[chat-debug] chatContent:initial-load-decision", {
            restoredGuestHistory,
            newSessionCreated,
            disableOneByOne,
            visibleMessageCount: messages.length,
          });

          if (restoredGuestHistory) {
            chatDebugLog('chatContent', "[chat-debug] chatContent:branch", {
              branch: "restoredGuestHistory",
            });
            allMessagesLoaded = true;
          } else if (newSessionCreated && !disableOneByOne) {
            chatDebugLog('chatContent', "[chat-debug] chatContent:branch", {
              branch: "loadMessagesOneByOne",
            });
            loadMessagesOneByOne(messages);
          } else {
            chatDebugLog('chatContent', "[chat-debug] chatContent:branch", {
              branch: "initVideoLoaders",
            });
            // Initialize video loaders to show individual spinners while buffering
            initVideoLoaders(contentWrapper, "spinner");
          }

            /**
             * Pending congratulation (AI generation)
             */
            if (response.data.pending_congratulation) {
              messageQueue.addToQueue(async () => {
                const pendingData = response.data.pending_congratulation;
                const chatId = contentWrapper.attr("id");
                const sessionToken = contentWrapper.attr("data-session-token");
                const userToken = contentWrapper.attr("data-user-token");

                // Show bubbles immediately when task starts
                const userMessageTemplate = appendUserMessage(contentWrapper);
                userMessageTemplate.show();
                scrollToChatBottom(100);

                await new Promise((resolveTask) => {
                  jQuery.ajax({
                    url: growtype_chat_ajax.url,
                    type: "post",
                    data: {
                      action: "growtype_chat_generate_purchase_congratulation",
                      pending_congratulation: pendingData,
                      user: {
                        token: userToken,
                      },
                      session: {
                        token: sessionToken,
                        bot_profile:
                          window.growtype_chat[chatId]?.["bot_profile"],
                      },
                      nonce: growtype_chat_ajax.nonce,
                    },
                    success: function (genResponse) {
                      if (genResponse.success) {
                        // renderMessage already returns the queue promise, but we want
                        // to wait for it within this task or just let it chain.
                        // Chaining is cleaner.
                        renderMessage(
                          contentWrapper,
                          genResponse,
                          userMessageTemplate
                        ).then(resolveTask);
                      } else {
                        console.error(
                          "Purchase congratulation generation failed:",
                          genResponse
                        );
                        userMessageTemplate.remove();
                        resolveTask();
                      }
                    },
                    error: function (xhr, status, error) {
                      console.error(
                        "Purchase congratulation AJAX error:",
                        error
                      );
                      userMessageTemplate.remove();
                      resolveTask();
                    },
                  });
                });
              });
            }

            initMessageActions(contentWrapper);

            /**
             * Pending message from last session (e.g. after login)
             */
            if (
              response.data.pending_message &&
              response.data.pending_message.value
            ) {
              // Clear any pre-filled textarea from the previous session
              clearPendingChatInput(contentWrapper);

              setTimeout(() => {
                document.dispatchEvent(
                  new CustomEvent("growtypeChatTriggerMessage", {
                    detail: {
                      message: response.data.pending_message.value,
                    },
                  })
                );
              }, 1000);
            }

            /**
             * Scroll to bottom (backup for lazy-loaded content)
             */
            if (
              $(".g-chatform-content .g-chatform-message:visible").length > 1
            ) {
              setTimeout(function () {
                scrollToChatBottom(0);
              }, 500);
            }

            if (contentWrapper.find(".lazy-load").length > 0) {
              if (typeof $.fn.Lazy === "function") {
                let chatLazySettings = {};
                chatLazySettings["appendScroll"] = contentWrapper.find(
                  ".g-chatform-content"
                );
                contentWrapper.find("img.lazy-load").Lazy(chatLazySettings);
                contentWrapper.find("video.lazy-load").Lazy(chatLazySettings);

                setTimeout(function () {
                  scrollToChatBottom(0);
                }, 1500);
              }
            }

            // Add scroll listener for pagination
            contentWrapper
              .find(".g-chatform-content")
              .off("scroll.chatContentPagination")
              .on("scroll.chatContentPagination", function () {
                if ($(this).scrollTop() === 0 && !allMessagesLoaded) {
                  loadMessages(true);
                }
              });

          }

          offset = hasBackendPagination
            ? Number(response.data.next_offset)
            : offset + limit;

          /**
           * Info modal
           */
          infoModal();

          /**
           * Init more options
           */
          initMoreOptions();

          isLoadingMore = false;

          /**
           * Content redered event
           */
          document.dispatchEvent(
            contentRenderedEvent({
              contentWrapper: contentWrapper,
            })
          );
        },
        error: function (xhrObject, status) {
          if (status === "abort") {
            chatDebugLog("chatContent", "[chat-debug] chatContent:request-aborted", {
              isLoadMore,
              limit,
              offset,
              requestGeneration,
              requestSignature,
            });
          }

          contentWrapper.find(".g-chat-loader-more").remove();
          isLoadingMore = false;
        },
        complete: function () {
          forgetPendingChatContentRequest(contentWrapper, xhr);
          contentWrapper.find(".g-chat-loader-more").remove();
          isLoadingMore = false;
        },
      };

      const prefetched = !isLoadMore
        ? window._growtypeChatShellInitialResponses?.content
        : null;
      if (prefetched) {
        delete window._growtypeChatShellInitialResponses.content;
        requestOptions.success(prefetched);
        return;
      }

      xhr = jQuery.ajax(requestOptions);

      rememberPendingChatContentRequest(contentWrapper, xhr);
    }

    // Initial load
    loadMessages(false);
  }
}

export function initMoreOptions() {
  /**
   * Clear session
   */
  clearSession($(".g-chat-btn-clear-session"));

  /**
   * Duplicate session
   */
  duplicateSession($(".g-chat-btn-duplicate-session"));
}

export function initMessageActions(wrapper, specificMessages = null) {
  let context = specificMessages ? specificMessages : wrapper;

  // If specificMessages is a set of elements, we need to find children within them or apply to them
  // But the helper functions usually take a selector or jQuery object of the buttons.
  // Let's just re-run for the whole wrapper or try to scope it.
  // Most init functions are idempotent or safe to re-run?
  // Let's look at existing code: playMessage(contentWrapper.find(...))

  // To be safe and simple, let's re-run for the whole wrapper for now,
  // or better, scope to the new messages if possible.
  // But the existing code runs on contentWrapper.find(...).

  // Let's replicate the init block

  messageActions(wrapper.find(".g-chatform-message-actions-selector"));
  playMessage(wrapper.find(".g-chatform-message-play"));
  convertMessageToImage(
    wrapper.find(".g-chatform-message-create-image .icon-wrapper")
  );
  pinMessage(wrapper.find(".g-chatform-message-pin"));
  regenerateWithFeedbackMessage(
    wrapper.find(".g-chatform-message-feedback-regenerate")
  );
  evaluateMessage(
    wrapper.find(
      ".g-chatform-message-feedback .g-chatform-message-actions-options-single"
    )
  );
  deleteMessage(wrapper.find(".g-chatform-message-delete"));
  shareMessage(wrapper.find(".g-chatform-message-share"));
  chatMysteryBox(wrapper.find(".btn-open-mystery-box"));

  if (!specificMessages) {
    // These only need to run once or on full init
    chatSettingsBtn();
    recordAudio();
    shareImage();
    chatMoreOptions();
    submitMessage();
    questionQuiz(wrapper);
    sampleActions();
    audiocall();
    videocall();
    emojiPicker();
    giftPicker();
    customUrlQueryMessage();
    chatSettings();
    evaluateChatHeight();

    new GenerateContent();

    wrapper.find(".audio-message").map(function (index, element) {
      renderAudio($(element));
    });

    document.dispatchEvent(
      initGrowtypeSidebarEvent({
        trigger: wrapper.find(".g-btn-close-sidebar"),
      })
    );

    wrapper.find(".growtype-theme-fancybox").each(function (index, element) {
      initFancybox(jQuery(element));
    });
  } else {
    // For new messages, init audio, fancybox and lazyload
    specificMessages.find(".audio-message").map(function (index, element) {
      renderAudio($(element));
    });
    specificMessages
      .find(".growtype-theme-fancybox")
      .each(function (index, element) {
        initFancybox(jQuery(element));
      });

    // Init lazyload
    if (typeof $.fn.Lazy === "function") {
      specificMessages.find("img.lazy-load, video.lazy-load").Lazy({
        appendScroll: wrapper.find(".g-chatform-content"),
      });
    }
  }
}
